import { Code, FormatQuote, Search, TextFields } from "@mui/icons-material";
import { Box, Button, Chip, IconButton, List, ListItem, ListItemText, Popover, TextField, Tooltip, Typography } from "@mui/material";
import { Editor } from "@tiptap/react";
import { useEffect, useState } from "react";

interface NoteBlocksProps {
    editor: Editor;
    mode: number;
}

 const languages = [
    'javascript', 'typescript', 'python', 'java', 'cpp', 'c', 'csharp',
    'css', 'scss', 'json', 'xml', 'yaml', 'markdown',
    'sql', 'php', 'ruby', 'go', 'rust', 'swift', 'kotlin',
    'bash', 'shell', 'powershell', 'dockerfile'
];

export default function NoteBlocks({ editor, mode }: NoteBlocksProps) {

    const [codeAnchorEl, setCodeAnchorEl] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [isBlockquoteActive, setIsBlockquoteActive] = useState(false);
    const [isCodeBlockActive, setIsCodeBlockActive] = useState(false);

    
    const getFilteredLanguages = () => {
        if (!searchTerm) {
            return languages;
        }
      
        const filtered = languages.filter(lang => 
            lang.toLowerCase().includes(searchTerm.toLowerCase())
        );
      
        return filtered;
    };
    
    const handleCodeBlockClick = (event: any) => {
        if (editor.isActive('codeBlock')) {
            editor.commands.toggleCodeBlock();
        } else {
            setCodeAnchorEl(event.currentTarget);
        }
    };
    
    const handleLanguageSelect = (language: any) => {
        editor.commands.toggleCodeBlock({ language });
        setCodeAnchorEl(null);
        setSearchTerm('');
    };
    
    const handleCodePopoverClose = () => {
        setCodeAnchorEl(null);
        setSearchTerm('');
    };

    useEffect(() => {
        if (!editor) return;

        const updateActiveStates = () => {
            setIsBlockquoteActive(editor.isActive('blockquote'));
            setIsCodeBlockActive(editor.isActive('codeBlock'));
        };

        editor.on('update', updateActiveStates);
        editor.on('selectionUpdate', updateActiveStates);

        return () => {
            editor.off('update', updateActiveStates);
            editor.off('selectionUpdate', updateActiveStates);
        };
    }, [editor]);
    
    return (
        <>
            <Tooltip title="BlockQuote">
                <IconButton
                    size="small"
                    onClick={() => {
                        editor.commands.toggleBlockquote();
                        setIsBlockquoteActive(!isBlockquoteActive);
                    }}
                    sx={{
                        backgroundColor: isBlockquoteActive ? '#e8f0fe' : 'transparent',
                        color: isBlockquoteActive ? (mode === 1 ? '#1a73e8' : "white") : (mode === 1 ? 'inherit' : 'white')
                    }}
                >
                    <FormatQuote sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="Code Block">
                <IconButton
                    size="small"
                    onClick={handleCodeBlockClick}
                    sx={{
                        backgroundColor: isCodeBlockActive ? '#e8f0fe' : 'transparent',
                        color: isCodeBlockActive ? (mode === 1 ? '#1a73e8' : "white") : (mode === 1 ? 'inherit' : 'white')
                    }}
                >
                    <Code sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>


            <Popover
                open={Boolean(codeAnchorEl)}
                anchorEl={codeAnchorEl}
                onClose={handleCodePopoverClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Select Programming Language
                    </Typography>
                    
                    
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search languages..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                    />
                    
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {(() => {
                            const filteredLanguages = getFilteredLanguages();
                            
                            return (
                                <>
                                    {(languages.length > 0 || searchTerm) && (
                                        <List dense sx={{ pt: 0 }}>
                                            {(searchTerm ? [...filteredLanguages] : languages)
                                                .map((language) => (
                                                <ListItem 
                                                key={language}
                                                onClick={() => handleLanguageSelect(language)}
                                                sx={{ 
                                                    py: 0.5,
                                                    px: 1,
                                                    borderRadius: 1,
                                                    '&:hover': { backgroundColor: 'action.hover' }
                                                }}
                                                >
                                                <ListItemText 
                                                    primary={language}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                </>
                            );
                        })()}
                    </Box>
                    
               
                    <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        <Button 
                            size="small" 
                            onClick={() => handleLanguageSelect('text')}
                            startIcon={<TextFields />}
                        >
                            Plain Text
                        </Button>
                        <Button 
                            size="small" 
                            onClick={handleCodePopoverClose}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}
